/**
 * HeroStatsSection - Premium Stats Display
 *
 * Premium fintech aesthetic - clean, minimal, dark accents.
 * Displays key metrics at the top of the journey.
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { HeroStats } from '../../../api/_schemas/journeyOutput.schema';

interface HeroStatsSectionProps {
  stats: HeroStats;
  className?: string;
}

// Animated counter hook
function useAnimatedCounter(target: number, duration: number = 2000): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (target === 0) {
      setCount(0);
      return;
    }

    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(target * easeOut));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration]);

  return count;
}

// Format large numbers in Indian style
function formatIndianNumber(num: number): string {
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(1)}Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num.toLocaleString('en-IN')}`;
}

// Status styles - matching Projections tab theme
const STATUS_STYLES = {
  achieved: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    label: 'Achieved',
  },
  on_track: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-600',
    label: 'On Track',
  },
  at_risk: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    label: 'At Risk',
  },
} as const;

// Confidence styles - emerald for high confidence like Projections
const CONFIDENCE_STYLES = {
  high: { color: 'text-emerald-600', bg: 'stroke-emerald-500', track: 'stroke-emerald-100', label: 'High Confidence' },
  medium: { color: 'text-amber-600', bg: 'stroke-amber-500', track: 'stroke-amber-100', label: 'Medium' },
  low: { color: 'text-red-600', bg: 'stroke-red-500', track: 'stroke-red-100', label: 'Review Needed' },
} as const;

// Stat card component
interface StatCardProps {
  title: string;
  children: React.ReactNode;
  subtitle?: string;
  icon: React.ReactNode;
  delay?: number;
  className?: string;
  highlight?: boolean;
}

function StatCard({ title, children, subtitle, icon, delay = 0, className, highlight }: StatCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: 'easeOut' }}
      className={cn(
        'relative rounded-lg p-5 transition-all',
        highlight
          ? 'bg-gradient-to-br from-emerald-600 to-emerald-700 text-white shadow-lg'
          : 'bg-white border border-slate-200',
        className
      )}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className={cn('w-5 h-5', highlight ? 'text-emerald-200' : 'text-slate-500')}>
          {icon}
        </span>
        <span className={cn(
          'text-xs font-medium uppercase tracking-wider',
          highlight ? 'text-emerald-200' : 'text-slate-500'
        )}>
          {title}
        </span>
      </div>
      <div className="space-y-1">
        {children}
        {subtitle && (
          <p className={cn('text-sm', highlight ? 'text-emerald-200' : 'text-slate-500')}>
            {subtitle}
          </p>
        )}
      </div>
    </motion.div>
  );
}

// Confidence ring
function ConfidenceRing({ score, level }: { score: number; level: 'high' | 'medium' | 'low' }) {
  const styles = CONFIDENCE_STYLES[level];
  const circumference = 2 * Math.PI * 28;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="relative w-16 h-16">
      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="28" fill="none" strokeWidth="4" className={styles.track} />
        <motion.circle
          cx="32" cy="32" r="28" fill="none" strokeWidth="4" strokeLinecap="round"
          className={styles.bg}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1.2, delay: 0.3, ease: 'easeOut' }}
          style={{ strokeDasharray: circumference }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={cn('text-lg font-semibold', styles.color)}>{score}%</span>
      </div>
    </div>
  );
}

// Confidence breakdown bar
function ConfidenceBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500 w-24 truncate">{label}</span>
      <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-slate-400 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 0.6, delay: 0.4, ease: 'easeOut' }}
        />
      </div>
      <span className="text-xs font-medium text-slate-600 w-8 text-right">{value}%</span>
    </div>
  );
}

// Icons as SVGs
const Icons = {
  savings: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  fire: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
    </svg>
  ),
  chart: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  shield: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  flag: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
    </svg>
  ),
};

export function HeroStatsSection({ stats, className }: HeroStatsSectionProps) {
  const [showConfidenceDetails, setShowConfidenceDetails] = useState(false);

  // Defaults
  const totalTaxSavings = stats.totalTaxSavings ?? { amountINR: 0, formatted: '₹0', comparedTo: '' };
  const timeToFIRE = stats.timeToFIRE ?? { months: 0, formatted: 'N/A', status: 'on_track' as const };
  const confidence = stats.confidence ?? { score: 50, level: 'medium' as const, explanation: '' };
  const rnorBenefit = stats.rnorBenefit ?? { eligible: false, years: 0, impactDescription: '' };

  const animatedSavings = useAnimatedCounter(totalTaxSavings.amountINR ?? 0, 2000);
  const fireStatus = STATUS_STYLES[timeToFIRE.status ?? 'on_track'] ?? STATUS_STYLES.on_track;
  const confidenceStyles = CONFIDENCE_STYLES[confidence.level ?? 'medium'] ?? CONFIDENCE_STYLES.medium;

  return (
    <div className={cn('space-y-6', className)}>
      {/* Headline */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-2">
          {stats.personalizedHeadline}
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto">
          {stats.personalizedSubheadline}
        </p>
      </motion.div>

      {/* SS Eligibility Alert */}
      {stats.ssEligibility && stats.ssEligibility.isHighValueOpportunity && !stats.ssEligibility.eligible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-slate-50 border border-slate-200 rounded-lg p-5"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-md bg-slate-200 flex items-center justify-center">
              {Icons.flag}
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-slate-800 mb-1">
                Social Security Opportunity
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                {stats.ssEligibility.recommendation}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <div className="bg-white rounded-md px-3 py-2 border border-slate-200">
                  <span className="text-xs text-slate-500 block">Years to Qualify</span>
                  <span className="text-xl font-semibold text-slate-800">
                    {stats.ssEligibility.yearsToQualify}
                  </span>
                </div>
                {stats.ssEligibility.lifetimeValueFormatted && (
                  <div className="bg-white rounded-md px-3 py-2 border border-slate-200">
                    <span className="text-xs text-slate-500 block">Lifetime Value</span>
                    <span className="text-xl font-semibold text-slate-800">
                      {stats.ssEligibility.lifetimeValueFormatted}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className={cn(
        'grid gap-4',
        stats.ssEligibility?.eligible ? 'grid-cols-2 lg:grid-cols-5' : 'grid-cols-2 lg:grid-cols-4'
      )}>
        {/* Tax Savings - Highlighted */}
        <StatCard
          title="Tax Savings"
          icon={Icons.savings}
          delay={0.1}
          highlight
          className="col-span-2 lg:col-span-1"
        >
          {totalTaxSavings.amountINR > 0 ? (
            <>
              <div className="text-3xl font-semibold tracking-tight">
                {formatIndianNumber(animatedSavings)}
              </div>
              <p className="text-sm text-emerald-200 mt-1">
                {totalTaxSavings.comparedTo}
              </p>
            </>
          ) : (
            <div className="space-y-2">
              <div className="text-2xl font-semibold text-emerald-200">₹0</div>
              <p className="text-sm text-emerald-200/70">with current timeline</p>
              {stats.ssEligibility && !stats.ssEligibility.eligible && stats.ssEligibility.yearsToQualify <= 3 && (
                <div className="mt-2 p-2 bg-emerald-800/50 rounded border border-emerald-500/30">
                  <p className="text-xs text-emerald-100">
                    Delay {stats.ssEligibility.yearsToQualify} year{stats.ssEligibility.yearsToQualify > 1 ? 's' : ''} to unlock SS benefits
                  </p>
                </div>
              )}
            </div>
          )}
        </StatCard>

        {/* Time to FIRE */}
        <StatCard title="Time to FIRE" icon={Icons.fire} delay={0.2}>
          <div className="text-2xl font-semibold text-slate-800">
            {timeToFIRE.formatted}
          </div>
          <div className="mt-2">
            <span className={cn(
              'inline-flex items-center px-2 py-1 rounded text-xs font-medium',
              fireStatus.bg, fireStatus.text
            )}>
              {fireStatus.label}
            </span>
          </div>
        </StatCard>

        {/* Confidence */}
        <StatCard title="Plan Confidence" icon={Icons.chart} delay={0.3}>
          <div className="flex items-center gap-3">
            <ConfidenceRing score={confidence.score} level={confidence.level} />
            <div className="flex-1 min-w-0">
              <p className={cn('text-sm font-medium', confidenceStyles.color)}>
                {confidenceStyles.label}
              </p>
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                {confidence.explanation}
              </p>
            </div>
          </div>

          {/* Details toggle */}
          {(confidence.components || confidence.limitations?.length || confidence.improvementSuggestions?.length) && (
            <button
              type="button"
              onClick={() => setShowConfidenceDetails(!showConfidenceDetails)}
              className="mt-3 w-full flex items-center justify-between text-xs text-slate-500 hover:text-slate-700 py-2 border-t border-slate-100"
            >
              <span className="font-medium">
                {showConfidenceDetails ? 'Hide details' : 'Show breakdown'}
              </span>
              <span className={cn('transition-transform', showConfidenceDetails && 'rotate-180')}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>
          )}

          {showConfidenceDetails && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-3 pt-2"
            >
              {confidence.components && (
                <div className="space-y-1.5">
                  <ConfidenceBar label="RNOR Eligibility" value={confidence.components.rnorCertainty} />
                  <ConfidenceBar label="Asset Data" value={confidence.components.assetDataCompleteness} />
                  <ConfidenceBar label="Tax Accuracy" value={confidence.components.taxCalculationAccuracy} />
                  <ConfidenceBar label="Projections" value={confidence.components.projectionReliability} />
                </div>
              )}

              {confidence.limitations && confidence.limitations.length > 0 && (
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-xs font-medium text-amber-600 mb-1">Limitations</p>
                  <ul className="space-y-1">
                    {confidence.limitations.map((l, i) => (
                      <li key={i} className="text-xs text-slate-500 flex items-start gap-1">
                        <span className="text-amber-500">•</span>
                        <span>{l}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {confidence.improvementSuggestions && confidence.improvementSuggestions.length > 0 && (
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-600 mb-1">Improve to 90%+</p>
                  <ol className="space-y-1">
                    {confidence.improvementSuggestions.map((s, i) => (
                      <li key={i} className="text-xs text-slate-500 flex items-start gap-1">
                        <span className="text-slate-400 font-medium">{i + 1}.</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </motion.div>
          )}
        </StatCard>

        {/* RNOR Window */}
        <StatCard title="RNOR Window" icon={Icons.shield} delay={0.4}>
          {rnorBenefit.eligible ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-slate-800">
                  {rnorBenefit.years}
                </span>
                <span className="text-slate-500">{rnorBenefit.years === 1 ? 'year' : 'years'}</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">{rnorBenefit.impactDescription}</p>
            </>
          ) : (
            <>
              <div className="text-lg font-medium text-slate-400">Not Eligible</div>
              <p className="text-sm text-slate-500 mt-1">{rnorBenefit.impactDescription}</p>
            </>
          )}
        </StatCard>

        {/* SS Status (if eligible) */}
        {stats.ssEligibility?.eligible && (
          <StatCard title="Social Security" icon={Icons.flag} delay={0.5}>
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-semibold text-slate-800">Eligible</span>
              <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            {stats.ssEligibility.lifetimeValueFormatted && (
              <p className="text-sm text-slate-500 mt-1">
                Est. lifetime: {stats.ssEligibility.lifetimeValueFormatted}
              </p>
            )}
          </StatCard>
        )}
      </div>

      {/* Trust indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="flex items-center justify-center gap-2 text-xs text-slate-400"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        <span>Based on your specific financial profile and tax rules</span>
      </motion.div>
    </div>
  );
}

export default HeroStatsSection;
